import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

interface ArchiveRow {
  _id: string;
  body: string;
  sender: string;
  groupName?: string;
  accountName?: string;
  isRideRelated: boolean;
  preFilterResult?: { confidence: number; type: string; reason?: string };
  aiResult?: { isRideRequest: boolean; confidence: number };
  receivedAt: string;
}

interface ArchiveResponse {
  messages: ArchiveRow[];
  pagination: {
    total: number;
    page: number;
    pages: number;
    limit: number;
  };
}

const fetchArchive = async (
  q: string,
  rideRelated: string,
  page: number
): Promise<ArchiveResponse> => {
  const params: Record<string, any> = { page, limit: 25 };
  if (q) params.q = q;
  if (rideRelated !== '') params.isRideRelated = rideRelated;
  const res = await api.get('/messages/archive', { params });
  return res.data;
};

const MessageArchive: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState('');
  const [rideRelated, setRideRelated] = useState('');
  const [page, setPage] = useState(1);

  const [selected, setSelected] = useState<string[]>([]);
  const [deleting, setDeleting] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const [viewMessage, setViewMessage] = useState<ArchiveRow | null>(null);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['messageArchive', q, rideRelated, page],
    queryFn: () => fetchArchive(q, rideRelated, page),
    keepPreviousData: true,
  });

  const handleSearch = () => {
    setQ(searchInput);
    setPage(1);
    setSelected([]);
  };

  const rows = data?.messages ?? [];

  const allOnPageSelected = rows.length > 0 && rows.every((r) => selected.indexOf(r._id) !== -1);
  const someOnPageSelected = rows.some((r) => selected.indexOf(r._id) !== -1);

  const toggleSelectAll = () => {
    if (allOnPageSelected) {
      setSelected((prev) => prev.filter((id) => rows.every((r) => r._id !== id)));
    } else {
      const newIds = rows.map((r) => r._id).filter((id) => selected.indexOf(id) === -1);
      setSelected((prev) => prev.concat(newIds));
    }
  };

  const toggleRow = (id: string) => {
    setSelected((prev) =>
      prev.indexOf(id) !== -1 ? prev.filter((x) => x !== id) : prev.concat([id])
    );
  };

  const handleDeleteSelected = async () => {
    setDeleting(true);
    try {
      await Promise.all(selected.map((id) => api.delete(`/messages/archive/${id}`)));
      setSelected([]);
      setConfirmDeleteOpen(false);
      queryClient.invalidateQueries({ queryKey: ['messageArchive'] });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h4" gutterBottom>
        Message Archive
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        30-day rolling archive of all processed WhatsApp messages.
      </Typography>

      {/* Filters */}
      <Stack direction="row" spacing={2} mb={3} flexWrap="wrap" alignItems="center" useFlexGap>
        <TextField
          size="small"
          placeholder="Search message text, group, sender…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          sx={{ minWidth: { xs: '100%', sm: 300 } }}
          InputProps={{
            endAdornment: (
              <IconButton size="small" onClick={handleSearch}>
                <SearchIcon fontSize="small" />
              </IconButton>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Type</InputLabel>
          <Select
            value={rideRelated}
            label="Type"
            onChange={(e) => { setRideRelated(e.target.value); setPage(1); setSelected([]); }}
          >
            <MenuItem value="">All messages</MenuItem>
            <MenuItem value="true">Ride-related only</MenuItem>
            <MenuItem value="false">Filtered out only</MenuItem>
          </Select>
        </FormControl>
        {data && (
          <Typography variant="body2" color="text.secondary">
            {data.pagination.total.toLocaleString()} message{data.pagination.total !== 1 ? 's' : ''}
          </Typography>
        )}
        {selected.length > 0 && (
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={() => setConfirmDeleteOpen(true)}
            disabled={deleting}
          >
            Delete {selected.length} selected
          </Button>
        )}
      </Stack>

      <Paper variant="outlined" sx={{ borderRadius: 2 }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" p={6}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer sx={{ opacity: isFetching ? 0.6 : 1, transition: 'opacity 0.2s', overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      indeterminate={someOnPageSelected && !allOnPageSelected}
                      checked={allOnPageSelected}
                      onChange={toggleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Message</TableCell>
                  <TableCell>Group / Account</TableCell>
                  <TableCell align="center">Type</TableCell>
                  <TableCell align="center">Pre-filter</TableCell>
                  <TableCell align="center">AI</TableCell>
                  <TableCell>Received</TableCell>
                  <TableCell padding="checkbox" />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((m) => (
                  <TableRow
                    key={m._id}
                    hover
                    selected={selected.indexOf(m._id) !== -1}
                    sx={{ cursor: 'default' }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selected.indexOf(m._id) !== -1}
                        onChange={() => toggleRow(m._id)}
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 280 }}>
                      <Tooltip title={m.body}>
                        <Typography
                          variant="body2"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: 260,
                            display: 'block',
                          }}
                        >
                          {m.body}
                        </Typography>
                      </Tooltip>
                      <Typography variant="caption" color="text.secondary">
                        {m.sender}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{m.groupName || '—'}</Typography>
                      {m.accountName && (
                        <Typography variant="caption" color="text.secondary">
                          {m.accountName}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={m.isRideRelated ? 'Ride' : 'Filtered'}
                        size="small"
                        color={m.isRideRelated ? 'success' : 'default'}
                        variant={m.isRideRelated ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {m.preFilterResult ? (
                        <Tooltip title={m.preFilterResult.reason || m.preFilterResult.type}>
                          <Chip
                            label={`${m.preFilterResult.confidence}%`}
                            size="small"
                            color={m.preFilterResult.confidence >= 60 ? 'primary' : 'default'}
                            icon={<InfoOutlinedIcon />}
                          />
                        </Tooltip>
                      ) : '—'}
                    </TableCell>
                    <TableCell align="center">
                      {m.aiResult ? (
                        <Chip
                          label={`${Math.round(m.aiResult.confidence)}%`}
                          size="small"
                          color={m.aiResult.isRideRequest ? 'success' : 'warning'}
                        />
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(m.receivedAt).toLocaleString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </Typography>
                    </TableCell>
                    <TableCell padding="checkbox">
                      <Tooltip title="View full message">
                        <IconButton size="small" onClick={() => setViewMessage(m)}>
                          <OpenInFullIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No messages found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {(data?.pagination.pages ?? 0) > 1 && (
          <Box display="flex" justifyContent="center" p={2}>
            <Pagination
              count={data!.pagination.pages}
              page={page}
              onChange={(_, p) => { setPage(p); setSelected([]); }}
              color="primary"
            />
          </Box>
        )}
      </Paper>

      {/* Confirm bulk delete */}
      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete {selected.length} message{selected.length !== 1 ? 's' : ''}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)} disabled={deleting}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteSelected}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* View full message dialog */}
      <Dialog
        open={viewMessage !== null}
        onClose={() => setViewMessage(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Full Message
          {viewMessage && (
            <Typography variant="caption" color="text.secondary" display="block">
              {viewMessage.groupName || 'Unknown group'} · {viewMessage.sender} ·{' '}
              {new Date(viewMessage.receivedAt).toLocaleString('en-IN')}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {viewMessage && (
            <>
              <Box
                sx={{
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                  p: 2,
                  fontFamily: 'monospace',
                  fontSize: 14,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  maxHeight: 400,
                  overflowY: 'auto',
                }}
              >
                {viewMessage.body}
              </Box>
              <Stack direction="row" spacing={1} mt={2} flexWrap="wrap" useFlexGap>
                <Chip
                  label={viewMessage.isRideRelated ? 'Ride-related' : 'Filtered out'}
                  size="small"
                  color={viewMessage.isRideRelated ? 'success' : 'default'}
                />
                {viewMessage.accountName && (
                  <Chip label={`Account: ${viewMessage.accountName}`} size="small" variant="outlined" />
                )}
                {viewMessage.aiResult && (
                  <Chip
                    label={`AI: ${Math.round(viewMessage.aiResult.confidence)}%`}
                    size="small"
                    color={viewMessage.aiResult.isRideRequest ? 'success' : 'warning'}
                  />
                )}
              </Stack>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewMessage(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MessageArchive;
